import React, { useState, useCallback, useEffect } from 'react';
import { useToast } from '../../../../hooks/utils/useToast';
import { useCourseSkillMapping } from '../../../../hooks/api/useCourseSkillMapping';
import {
  Search,
  RefreshCw,
  Users,
  X,
  Check,
  BookOpen,
  CheckCircle,
  AlertCircle,
  Brain,
  Filter,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import GenericDropdown from '../../../../components/GenericDropdown';
import CategoryDropdown from '../../../../components/CategoryDropdown';

const SkillMapping = () => {
  const { toast, showToast } = useToast();
  
  // Use custom hook for API operations
  const {
    skills,
    courses,
    allCourses,
    courseTypes,
    courseLevels,
    subcategories,
    skillCourses,
    loading,
    loadingCourses,
    error,
    skillsPagination,
    changeSkillsPageSize,
    pagination,
    paginateCourses,
    changePageSize,
    fetchSkillById,
    fetchCoursesWithFilters,
    fetchAllSkills,
    assignCoursesToSkill,
    // Skill mapping methods
    getSkillMappingAssignments,
    assignSkillMapping,
    getSkillMappingAvailableItems
  } = useCourseSkillMapping();
  
  // Component-specific state
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [showCourseAssignmentModal, setShowCourseAssignmentModal] = useState(false);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [activeModalTab, setActiveModalTab] = useState('assign'); // 'assign' or 'selected'
  
  // Skill mapping state (similar to featured marking)
  const [showSkillMappingModal, setShowSkillMappingModal] = useState(false);
  const [selectedSkillForMapping, setSelectedSkillForMapping] = useState(null);
  const [mappingType, setMappingType] = useState('type'); // course, category, career
  const [assignedMappingItems, setAssignedMappingItems] = useState([]);
  const [loadingMappingAssignments, setLoadingMappingAssignments] = useState(false);
  const [searchTermMapping, setSearchTermMapping] = useState('');
  const [availableMappingItems, setAvailableMappingItems] = useState([]);
  const [loadingMappingAvailable, setLoadingMappingAvailable] = useState(false);
  const [selectedCourseTypeForMapping, setSelectedCourseTypeForMapping] = useState(0);
  
  // Course assignment filters
  const [courseFilters, setCourseFilters] = useState({
    courseTypeId: '',
    categoryId: '',
    courseLevelId: ''
  });

  // Search and view states
  const [searchTerm, setSearchTerm] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  // API functions are now handled by the custom hook

  // Initialize data is now handled by the custom hook

  // Filter skills from allSkills for search functionality - now handled by backend
  const filteredSkills = Array.isArray(skills) ? skills : [];

  // Apply pagination to filtered skills
  const paginatedFilteredSkills = Array.isArray(filteredSkills) ? filteredSkills : [];

  // Handle pagination for filtered skills - now uses backend pagination
  const handlePaginateSkills = (page) => {
    const totalPages = skillsPagination.totalPages;
    if (page >= 1 && page <= totalPages) {
      fetchAllSkills({ page, pageSize: skillsPagination.pageSize, search: searchTerm });
    }
  };

  // Debounced search - only triggers when user stops typing
  useEffect(() => {
    // Only set up timeout if there's a search term
    if (searchTerm.trim() === '') {
      // If search is empty, reset to first page and fetch all
      setIsTyping(false);
      fetchAllSkills({ page: 1, pageSize: skillsPagination.pageSize, search: '' });
      return;
    }

    // User is typing
    setIsTyping(true);

    const timeoutId = setTimeout(() => {
      // User stopped typing, set isTyping to false and fetch
      setIsTyping(false);
      // Only fetch if the search term hasn't changed in the last 800ms
      fetchAllSkills({ page: 1, pageSize: skillsPagination.pageSize, search: searchTerm });
    }, 800); // 800ms delay - only triggers when user stops typing

    return () => {
      clearTimeout(timeoutId);
      // Clear typing indicator when component unmounts or search changes
      setIsTyping(false);
    };
  }, [searchTerm, skillsPagination.pageSize, fetchAllSkills]);

  // Handle course filter changes
  const handleCourseFilterChange = useCallback(async (filterType, value) => {
    const newFilters = { ...courseFilters, [filterType]: value };
    setCourseFilters(newFilters);
    
    const apiFilters = {};
    if (newFilters.courseTypeId && newFilters.courseTypeId !== '') {
      apiFilters.CourseTypeId = newFilters.courseTypeId;
    }
    if (newFilters.categoryId && newFilters.categoryId !== '') {
      apiFilters.CategoryId = newFilters.categoryId;
    }
    if (newFilters.courseLevelId && newFilters.courseLevelId !== '') {
      apiFilters.CourseLevelId = newFilters.courseLevelId;
    }
    
    await fetchCoursesWithFilters(apiFilters);
  }, [courseFilters, fetchCoursesWithFilters]);

  // Open course assignment modal
  const openCourseAssignmentModal = async (skill) => {
    setSelectedSkill(skill);
    setSelectedCourses([]);
    setShowCourseAssignmentModal(true);
    setCourseFilters({
      courseTypeId: '',
      categoryId: '',
      courseLevelId: ''
    });
    
    // Fetch skill details to get current course assignments
    try {
      await fetchSkillById(skill.skillId);
      // Automatically load all courses when modal opens
      await fetchCoursesWithFilters({});
    } catch (err) {
      console.error('Failed to fetch skill details:', err);
    }
  };

  // Close modal
  const closeModals = () => {
    setShowCourseAssignmentModal(false);
    setSelectedSkill(null);
    setSelectedCourses([]);
  };

  // Toggle course selection
  const toggleCourseSelection = (courseId) => {
    const isAssigned = skillCourses[selectedSkill?.skillId]?.includes(courseId) || false;
    
    setSelectedCourses(prev => {
      if (prev.includes(courseId)) {
        return prev.filter(id => id !== courseId);
      } else if (isAssigned) {
        return [...prev, courseId];
      } else {
        return [...prev, courseId];
      }
    });
  };

  // Handle course assignment
  const handleCourseAssignment = async () => {
    if (!selectedSkill || selectedCourses.length === 0) return;
    
    try {
      // Get current assigned courses
      const currentAssigned = skillCourses[selectedSkill.skillId] || [];
      
      // Calculate new assignment
      let newAssignment = [...currentAssigned];
      
      selectedCourses.forEach(courseId => {
        if (currentAssigned.includes(courseId)) {
          // Remove if currently assigned
          newAssignment = newAssignment.filter(id => id !== courseId);
        } else {
          // Add if not currently assigned
          newAssignment.push(courseId);
        }
      });
      
      await assignCoursesToSkill(selectedSkill.skillId, newAssignment, searchTerm, skillsPagination.currentPage, skillsPagination.pageSize);
      showToast('Courses assigned to skill successfully!', 'success');
      setSelectedCourses([]);
      closeModals();
    } catch (err) {
      showToast('Failed to assign courses to skill', 'error');
      console.error('Course assignment failed:', err);
    }
  };

  // Get skill courses
  const getSkillCourses = (skillId) => {
    return skillCourses[skillId] || [];
  };

  // Skill mapping functions (similar to featured marking)
  
  // Fetch assigned mapping items
  const fetchAssignedMappingItems = useCallback(async (skillId, type) => {
    if (!type || type === 'type') {
      setAssignedMappingItems([]);
      return;
    }

    setLoadingMappingAssignments(true);
    try {
      const typeId = type === 'course' ? 1 : type === 'career' ? 2 : 1;
      const result = await getSkillMappingAssignments(skillId, typeId);
      
      setAssignedMappingItems(result || []);
    } catch (err) {
      console.error('Failed to fetch assigned mapping items:', err);
      setAssignedMappingItems([]);
    } finally {
      setLoadingMappingAssignments(false);
    }
  }, [getSkillMappingAssignments]);

  // Open skill mapping modal
  const openSkillMappingModal = useCallback((skill) => {
    setSelectedSkillForMapping(skill);
    setMappingType('type');
    setShowSkillMappingModal(true);
    setAssignedMappingItems([]);
    setSearchTermMapping('');
    setAvailableMappingItems([]);
  }, []);

  // Close skill mapping modal
  const closeSkillMappingModal = useCallback(() => {
    setShowSkillMappingModal(false);
    setSelectedSkillForMapping(null);
    setMappingType('type');
    setAssignedMappingItems([]);
    setSearchTermMapping('');
    setAvailableMappingItems([]);
  }, []);

  // Handle mapping type change
  const handleMappingTypeChange = useCallback((type) => {
    setMappingType(type);
    setAssignedMappingItems([]);
    setAvailableMappingItems([]);
    setSearchTermMapping('');

    if (selectedSkillForMapping && type !== 'type') {
      fetchAssignedMappingItems(selectedSkillForMapping.id, type);
    }
  }, [selectedSkillForMapping, fetchAssignedMappingItems]);

  // Search available mapping items
  const fetchAvailableMappingItems = useCallback(async (type, searchTerm = '') => {
    if (!type || type === 'type') {
      setAvailableMappingItems([]);
      return;
    }

    if (!searchTerm || searchTerm.trim() === '') {
      setAvailableMappingItems([]);
      return;
    }
    
    setLoadingMappingAvailable(true);
    try {
      const assignedIds = assignedMappingItems.map(item => item.id);
      const result = await getSkillMappingAvailableItems(type, searchTerm, assignedIds);
      
      setAvailableMappingItems(result || []);
    } catch (err) {
      console.error('Failed to fetch available mapping items:', err);
      setAvailableMappingItems([]);
    } finally {
      setLoadingMappingAvailable(false);
    }
  }, [getSkillMappingAvailableItems, assignedMappingItems]);

  // Toggle mapping item assignment
  const toggleMappingItemAssignment = useCallback((item) => {
    const isAssigned = assignedMappingItems.some(assigned => assigned.id === item.id);
    
    if (isAssigned) {
      setAssignedMappingItems(prev => prev.filter(assigned => assigned.id !== item.id));
    } else {
      setAssignedMappingItems(prev => [...prev, item]);
    }
  }, [assignedMappingItems]);

  // Handle skill mapping assignment
  const handleSkillMappingAssignment = useCallback(async () => {
    if (!selectedSkillForMapping || assignedMappingItems.length === 0) {
      showToast('Please select at least one item to assign', 'error');
      return;
    }

    try {
      if (!mappingType || mappingType === 'type') {
        showToast('Please select a type of mapping', 'error');
        return;
      }

      const typeId = mappingType === 'course' ? 1 : mappingType === 'career' ? 2 : 1;
      let convertedIds = assignedMappingItems.map(item => item.id);
      
      // Convert IDs based on type requirements
      switch (typeId) {
        case 1: // Courses - use long IDs
          convertedIds = convertedIds.map(id => parseInt(id));
          break;
        case 2: // Career Paths - use int IDs
          convertedIds = convertedIds.map(id => parseInt(id));
          break;
        default:
          throw new Error('Invalid type');
      }
      
      await assignSkillMapping(selectedSkillForMapping.id, typeId, convertedIds);
      showToast(`Skill assigned to ${assignedMappingItems.length} ${mappingType}(s) successfully!`, 'success');
      closeSkillMappingModal();
    } catch (err) {
      showToast('Failed to assign skill mapping', 'error');
    }
  }, [selectedSkillForMapping, assignedMappingItems, mappingType, assignSkillMapping, showToast, closeSkillMappingModal]);

  // Load initial data when modal opens
  useEffect(() => {
    if (showSkillMappingModal && selectedSkillForMapping && mappingType !== 'type') {
      fetchAssignedMappingItems(selectedSkillForMapping.id, mappingType);
    }
  }, [showSkillMappingModal, selectedSkillForMapping, mappingType, fetchAssignedMappingItems]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-600 dark:text-gray-300">Loading skills...</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg transform transition-all duration-300 ${
          toast.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
        }`}>
          <div className="flex items-center space-x-2">
            {toast.type === 'success' ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            <span className="font-medium">{toast.message}</span>
          </div>
        </div>
      )}
      
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-3 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl shadow-lg">
                  <Brain className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
                    Skill Mapping
                  </h1>
                  <p className="text-gray-600 dark:text-gray-400 mt-1 flex items-center gap-2">
                    <span className="inline-flex items-center px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs font-medium rounded-full">
                      {skillsPagination.totalItems || 0} Skills
                    </span>
                    <span className="text-gray-400">•</span>
                    <span>Manage skill assignments for courses and career paths</span>
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => window.location.href = '/admin/career-skills'}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <Brain className="w-5 h-5" />
                Manage Skills
              </button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 relative">
            <div className="bg-gradient-to-r from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 border border-red-200 dark:border-red-800 rounded-xl p-4 shadow-lg">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-red-100 dark:bg-red-900/50 rounded-full flex items-center justify-center">
                    <X className="w-5 h-5 text-red-600 dark:text-red-400" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
                  <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters Bar */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg mb-6 p-6 border border-gray-100 dark:border-gray-700">
          <div className="flex flex-col lg:flex-row gap-6 items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4 flex-1">
              {/* Search */}
              <div className="relative flex-1 max-w-2xl">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-5 h-5" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={isTyping ? "Type to search..." : "Search skills by title..."}
                  className={`w-full pl-12 pr-12 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-transparent transition-all duration-200 shadow-sm ${
                    isTyping 
                      ? 'border-purple-400 bg-purple-50 dark:bg-purple-900/20 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400' 
                      : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400'
                  }`}
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  {isTyping && (
                    <div className="flex items-center space-x-1">
                      <div className="flex space-x-1">
                        <div className="w-1 h-1 bg-purple-600 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                        <div className="w-1 h-1 bg-purple-600 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                        <div className="w-1 h-1 bg-purple-600 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
                      </div>
                    </div>
                  )}
                  {loading && !isTyping && searchTerm && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                  )}
                  {!isTyping && !loading && searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {isTyping && (
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Searching when you stop typing...
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Skills Display */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {paginatedFilteredSkills.map(skill => (
            <div key={skill.id} className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden hover:shadow-lg transition-shadow">
              {/* Skill Header */}
              <div className="relative h-32 bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/20 dark:to-pink-900/20">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-4xl mb-2">🎯</div>
                    <span className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-700">
                      {skill.title}
                    </span>
                  </div>
                </div>
              </div>

              {/* Skill Info */}
              <div className="p-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{skill.title}</h3>
                
                <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 mb-4">
                  <span>ID: {skill.id}</span>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => openSkillMappingModal(skill)}
                      className="p-2 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
                      title="Skill Mapping"
                    >
                      <Brain className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {paginatedFilteredSkills.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Brain className="w-8 h-8 text-gray-400 dark:text-gray-500" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No skills found</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              {searchTerm ? 'Try adjusting your search' : 'No skills available'}
            </p>
          </div>
        )}

        {/* Skills Pagination */}
        {skillsPagination.totalPages > 1 && (
          <div className="flex items-center justify-center py-4">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePaginateSkills(skillsPagination.currentPage - 1)}
                disabled={skillsPagination.currentPage === 1}
                className="flex items-center px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Previous
              </button>
              
              <button
                onClick={() => handlePaginateSkills(skillsPagination.currentPage + 1)}
                disabled={skillsPagination.currentPage === skillsPagination.totalPages}
                className="flex items-center px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-2" />
              </button>
            </div>
          </div>
        )}

        {/* Course Assignment Modal */}
        {showCourseAssignmentModal && selectedSkill && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    Manage Courses for {selectedSkill.title}
                  </h2>
                  <button
                    onClick={closeModals}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Modal Tabs */}
              <div className="border-b border-gray-200 dark:border-gray-700">
                <nav className="flex -mb-px">
                  <button
                    onClick={() => setActiveModalTab('assign')}
                    className={`py-3 px-6 text-sm font-medium border-b-2 transition-colors ${
                      activeModalTab === 'assign'
                        ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                  >
                    Assign Courses
                  </button>
                  <button
                    onClick={() => setActiveModalTab('selected')}
                    className={`py-3 px-6 text-sm font-medium border-b-2 transition-colors ${
                      activeModalTab === 'selected'
                        ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                  >
                    Selected Courses ({getSkillCourses(selectedSkill.skillId).length})
                  </button>
                </nav>
              </div>

              {/* Course Filters - Only show on Assign tab */}
              {activeModalTab === 'assign' && (
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Filter Courses</h3>
                    <button
                      onClick={() => {
                        setCourseFilters({
                          courseTypeId: '',
                          categoryId: '',
                          courseLevelId: ''
                        });
                      }}
                      className="text-sm text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300"
                    >
                      Clear Filters
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Course Type
                      </label>
                      <GenericDropdown
                        items={courseTypes}
                        value={courseFilters.courseTypeId}
                        onChange={(value) => handleCourseFilterChange('courseTypeId', value)}
                        placeholder="All Course Types"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Category
                      </label>
                      <CategoryDropdown
                        categories={subcategories}
                        value={courseFilters.categoryId}
                        onChange={(value) => handleCourseFilterChange('categoryId', value)}
                        placeholder="All Categories"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Course Level
                      </label>
                      <GenericDropdown
                        items={courseLevels}
                        value={courseFilters.courseLevelId}
                        onChange={(value) => handleCourseFilterChange('courseLevelId', value)}
                        placeholder="All Levels"
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="p-6 flex-1 overflow-hidden flex flex-col">
                {/* Assign Courses Tab Content */}
                {activeModalTab === 'assign' && (
                  <>
                    <div className="mb-4">
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Currently assigned: {getSkillCourses(selectedSkill.skillId).length} courses
                      </p>
                    </div>

                    <div className="space-y-2 flex-1 overflow-y-auto mb-4">
                      {loadingCourses ? (
                        <div className="flex items-center justify-center py-8">
                          <RefreshCw className="w-6 h-6 animate-spin text-purple-500 mr-2" />
                          <span className="text-gray-600 dark:text-gray-300">loading courses...</span>
                        </div>
                      ) : (
                        <>
                          {Array.isArray(courses) && courses.map(course => {
                            const isAssigned = getSkillCourses(selectedSkill.skillId).includes(course.id);
                            const isSelected = selectedCourses.includes(course.id);
                            const isCurrentlySelected = (isAssigned && !isSelected) || (!isAssigned && isSelected);
                            
                            return (
                              <div
                                key={course.id}
                                className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                                  isCurrentlySelected
                                    ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-300 dark:border-purple-700'
                                    : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                                }`}
                                onClick={() => toggleCourseSelection(course.id)}
                              >
                                <input
                                  type="checkbox"
                                  checked={isCurrentlySelected}
                                  onChange={() => toggleCourseSelection(course.id)}
                                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded mr-3"
                                />
                                <div className="flex-1">
                                  <div className="font-medium text-gray-900 dark:text-white">{course.title || course.name || 'Unknown Course'}</div>
                                  <div className="text-sm text-gray-500 dark:text-gray-400">
                                    {(() => {
                                      const courseType = courseTypes.find(ct => ct.id === course.courseTypeId);
                                      const category = subcategories.find(cat => cat.id === course.categoryId);
                                      const courseLevel = courseLevels.find(cl => cl.id === course.courseLevelId);
                                      
                                      const parts = [];
                                      // Add course description if available
                                      if (course.description) {
                                        parts.push(course.description);
                                      }
                                      if (courseType?.name) parts.push(courseType.name);
                                      if (category?.name) parts.push(category.name);
                                      if (courseLevel?.name) parts.push(courseLevel.name);
                                      
                                      return parts.length > 0 ? parts.join(' • ') : 'No additional info';
                                    })()}
                                  </div>
                                </div>
                                {isAssigned && !isSelected && (
                                  <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-1 rounded">
                                    Assigned
                                  </span>
                                )}
                                {isSelected && (
                                  <span className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 px-2 py-1 rounded">
                                    {isAssigned ? 'Deselected' : 'Selected'}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                          {(!Array.isArray(courses) || courses.length === 0) && !loadingCourses && (
                            <div className="text-center py-8">
                              <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                                {Object.keys(courseFilters).some(key => courseFilters[key]) 
                                  ? 'No courses found matching your filters' 
                                  : 'All courses loaded'}
                              </h3>
                              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                                {Object.keys(courseFilters).some(key => courseFilters[key]) 
                                  ? 'Try adjusting your filter criteria to see more courses.' 
                                  : `${Array.isArray(allCourses) ? allCourses.length : 0} courses are available. Use filters above to narrow down the selection.`}
                              </p>
                              {Object.keys(courseFilters).some(key => courseFilters[key]) && (
                                <button
                                  onClick={() => {
                                    setCourseFilters({
                                      courseTypeId: '',
                                      categoryId: '',
                                      courseLevelId: ''
                                    });
                                  }}
                                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                                >
                                  Clear Filters
                                </button>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Pagination */}
                    {pagination.totalPages > 1 && (
                      <div className="flex items-center justify-between py-4 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            Showing {((pagination.currentPage - 1) * pagination.pageSize) + 1} to{' '}
                            {Math.min(pagination.currentPage * pagination.pageSize, pagination.totalItems)} of{' '}
                            {pagination.totalItems} courses
                          </span>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-2">
                            <label className="text-sm text-gray-700 dark:text-gray-300">Show:</label>
                            <select
                              value={pagination.pageSize}
                              onChange={(e) => changePageSize(Number(e.target.value))}
                              className="border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            >
                              <option value={5}>5</option>
                              <option value={10}>10</option>
                              <option value={20}>20</option>
                              <option value={50}>50</option>
                            </select>
                          </div>
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={() => paginateCourses(pagination.currentPage - 1)}
                              disabled={pagination.currentPage === 1}
                              className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                            >
                              Previous
                            </button>
                            <span className="px-3 py-1 text-sm text-gray-700 dark:text-gray-300">
                              Page {pagination.currentPage} of {pagination.totalPages}
                            </span>
                            <button
                              onClick={() => paginateCourses(pagination.currentPage + 1)}
                              disabled={pagination.currentPage === pagination.totalPages}
                              className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <button
                        onClick={closeModals}
                        className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleCourseAssignment}
                        disabled={selectedCourses.length === 0}
                        className={`px-4 py-2 rounded-md transition-colors ${
                          selectedCourses.length === 0
                            ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                            : 'bg-purple-600 text-white hover:bg-purple-700'
                        }`}
                      >
                        {selectedCourses.length === 0 ? 'Select Courses to Assign' : `Update ${selectedCourses.length} Course${selectedCourses.length !== 1 ? 's' : ''}`}
                      </button>
                    </div>
                  </>
                )}

                {/* Selected Courses Tab Content */}
                {activeModalTab === 'selected' && (
                  <>
                    <div className="mb-4">
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Showing {getSkillCourses(selectedSkill.skillId).length} assigned courses
                      </p>
                    </div>

                    <div className="space-y-2 flex-1 overflow-y-auto">
                      {getSkillCourses(selectedSkill.skillId).length === 0 ? (
                        <div className="text-center py-12">
                          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <BookOpen className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                          </div>
                          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No courses assigned</h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                            Switch to the "Assign Courses" tab to add courses to this skill.
                          </p>
                          <button
                            onClick={() => setActiveModalTab('assign')}
                            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                          >
                            Assign Courses
                          </button>
                        </div>
                      ) : courses.length === 0 ? (
                        <div className="text-center py-12">
                          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <Filter className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                          </div>
                          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Course details not loaded</h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                            {getSkillCourses(selectedSkill.skillId).length} course(s) assigned. Apply filters in the "Assign Courses" tab to load course details.
                          </p>
                          <button
                            onClick={() => setActiveModalTab('assign')}
                            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                          >
                            Apply Filters to Load Courses
                          </button>
                        </div>
                      ) : (
                        getSkillCourses(selectedSkill.skillId).map(courseId => {
                          // Find the course details from the courses array if available
                          const course = courses.find(c => c.id === courseId);
                          return (
                            <div
                              key={courseId}
                              className="flex items-center p-4 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                            >
                              <div className="flex-1">
                                <div className="font-medium text-gray-900 dark:text-white">
                                  {course?.title || course?.name || `Course ID: ${courseId}`}
                                </div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  {course && (() => {
                                    const courseType = courseTypes.find(ct => ct.id === course.courseTypeId);
                                    const category = subcategories.find(cat => cat.id === course.categoryId);
                                    const courseLevel = courseLevels.find(cl => cl.id === course.courseLevelId);
                                    
                                    const parts = [];
                                    // Add course description if available
                                    if (course.description) {
                                      parts.push(course.description);
                                    }
                                    if (courseType?.name) parts.push(courseType.name);
                                    if (category?.name) parts.push(category.name);
                                    if (courseLevel?.name) parts.push(courseLevel.name);
                                    
                                    return parts.length > 0 ? parts.join(' • ') : 'Course details not available';
                                  })()}
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-1 rounded">
                                  Assigned
                                </span>
                                <button
                                  onClick={() => {
                                    // Switch to assign tab and pre-select this course for removal
                                    setActiveModalTab('assign');
                                    setSelectedCourses([courseId]);
                                  }}
                                  className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-sm"
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>

                    <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <button
                        onClick={closeModals}
                        className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                      >
                        Close
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Skill Mapping Modal */}
        {showSkillMappingModal && selectedSkillForMapping && (
          <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-screen overflow-y-auto transform transition-all">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div className="flex items-center">
                  <div className="bg-green-100 p-2 rounded-lg mr-3">
                    <Brain className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Skill Mapping</h2>
                    <p className="text-sm text-gray-600 mt-1">
                      Map "{selectedSkillForMapping.title}" to {mappingType}s
                    </p>
                  </div>
                </div>
                <button
                  onClick={closeSkillMappingModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X size={20} className="text-gray-500" />
                </button>
              </div>

              <div className="p-6">
                {/* Type Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Type of Mapping
                  </label>
                  <select
                    value={mappingType}
                    onChange={(e) => handleMappingTypeChange(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
                  >
                    <option value="type">Select type</option>
                    <option value="course">Course</option>
                    <option value="career">Career Path</option>
                  </select>
                </div>

                {/* Course Type Filter - Only show when mappingType is 'course' */}
                {mappingType === 'course' && (
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Course Type
                    </label>
                    <GenericDropdown
                      items={[
                        { id: 0, name: 'All Course Types' },
                        ...courseTypes
                      ]}
                      value={selectedCourseTypeForMapping}
                      onChange={(value) => {
                        setSelectedCourseTypeForMapping(value);
                        // Trigger search with new course type filter
                        if (searchTermMapping) {
                          fetchAvailableMappingItems('course', searchTermMapping, value);
                        }
                      }}
                      placeholder="Select course type"
                    />
                  </div>
                )}

                {/* Search */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Search {mappingType}s
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder={`Search ${mappingType}s by name...`}
                      value={searchTermMapping}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSearchTermMapping(value);
                        // Trigger search immediately with the selected mapping type
                        fetchAvailableMappingItems(mappingType, value, selectedCourseTypeForMapping);
                      }}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
                    />
                    {loadingMappingAvailable && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-600"></div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Available Items List */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-semibold text-gray-700">
                      Available {mappingType}s
                    </label>
                    <span className="text-sm text-gray-600">
                      {availableMappingItems.length} found
                    </span>
                  </div>
                  
                  <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
                    {loadingMappingAvailable ? (
                      <div className="flex items-center justify-center p-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
                        <span className="ml-3 text-gray-600">Searching...</span>
                      </div>
                    ) : availableMappingItems.length === 0 ? (
                      <div className="text-center p-8 text-gray-500">
                        No {mappingType}s found
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-200">
                        {availableMappingItems
                          .filter(item => {
                            // Only show items that match current mapping type
                            return (item.title && mappingType === 'course') || 
                                   (item.title && mappingType === 'career');
                          })
                          .map((item) => {
                          const isAssigned = assignedMappingItems.some(assigned => assigned.id === item.id);
                          
                          return (
                            <div
                              key={item.id}
                              onClick={() => toggleMappingItemAssignment(item)}
                              className={`p-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                                isAssigned ? 'bg-green-50 border-l-4 border-green-500' : ''
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={isAssigned}
                                    onChange={() => {}}
                                    className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500 mr-3"
                                  />
                                  <div>
                                    <p className="font-medium text-gray-900">
                                      {item.title || item.name}
                                    </p>
                                    <p className="text-sm text-gray-600">
                                      {item.description || `${mappingType} ID: ${item.id}`}
                                    </p>
                                  </div>
                                </div>
                                {isAssigned && (
                                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                                    Assigned
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Assigned Items List */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-semibold text-gray-700">
                      Assigned {mappingType}s
                    </label>
                    <span className="text-sm text-gray-600">
                      {assignedMappingItems.length} total
                    </span>
                  </div>
                  
                  <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
                    {loadingMappingAssignments ? (
                      <div className="flex items-center justify-center p-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
                        <span className="ml-3 text-gray-600">Loading...</span>
                      </div>
                    ) : assignedMappingItems.length === 0 ? (
                      <div className="text-center p-8 text-gray-500">
                        No {mappingType}s assigned to this skill
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-200">
                        {assignedMappingItems.map((item) => (
                          <div
                            key={item.id}
                            className="p-3 hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={true}
                                  onChange={() => toggleMappingItemAssignment(item)}
                                  className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500 mr-3"
                                />
                                <div>
                                  <p className="font-medium text-gray-900">
                                    {item.title || item.name}
                                  </p>
                                  <p className="text-sm text-gray-600">
                                    {item.description || `${mappingType} ID: ${item.id}`}
                                  </p>
                                </div>
                              </div>
                              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                                Assigned
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={closeSkillMappingModal}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    onClick={handleSkillMappingAssignment}
                    disabled={assignedMappingItems.length === 0}
                    className="px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all duration-200 font-medium shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Update Mappings
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SkillMapping;
